import { Card } from 'nhsuk-react-components';
import {
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { RuleFormValues, RuleForm } from 'components/forms/Rule/RuleForm';
import useCampaign from 'hooks/useCampaign.ts';
import {
  buildHrefForSuccessfulRuleSubmission,
  getIterationAndRuleByIDs,
  updateRule,
  UpdateRulePayload,
} from 'utils/ruleUtils.ts';
import Back from 'components/Back/index.tsx';
import IterationPageHeader from 'components/IterationPageHeader/index.tsx';
import useProtectRoute from 'hooks/useProtectRoute.ts';
import ProgressIndicator from 'components/ProgressIndicator/index.tsx';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import useUpdateIteration from 'queries/mutate/updateIteration.ts';
import { IterationDetailsFormValues } from 'schemas/Iteration.schema.ts';
import { getIterationById } from 'utils/iterationUtils.ts';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign.ts';
import useFetchTemplateForCampaign from 'queries/fetch/fetchTemplateForCampaign.ts';
import { RuleType, ruleTypeMap } from 'schemas/IterationRule.schema.ts';
import { AllowedTemplateKeys } from 'schemas/CampaignTemplates.schema.ts';
import useReferenceData from 'hooks/useReferenceData.ts';
import { LibraryRuleMetadata } from 'schemas/LibraryRule.schema';
import useFetchTeamDefaultRules from 'queries/fetch/fetchTeamDefaultRules';
import { useState } from 'react';
import { ApiError } from 'schemas/ApiError.schema';
import { API_UPDATE_RULES_ERROR } from 'constants/errorMessages';
import { buildRulePathParam } from 'utils/navigationUtils';

export type RuleLocationState = {
    addCustomRule: boolean
    libraryRule?: LibraryRuleMetadata
}

const RULE_TYPE_DISPLAY_KEY: Record<RuleType, AllowedTemplateKeys> = {
  F: 'IterationDetails.IterationRules.filters.input.display',
  S: 'IterationDetails.IterationRules.suppressions.input.display',
  R: 'IterationDetails.IterationRules.routing.input.display',
};

export default function RulePage({ ruleTypeFromRoute }: {ruleTypeFromRoute: RuleType}) {
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const state = location.state as RuleLocationState | undefined;

  const [error, setError] = useState<ApiError | null>(null);
  const {
    teamId,
    campaignId,
    iterationId,
    ruleId,
    andClauseNumber,
  } = useParams();

  const searchParams = new URLSearchParams(location.search);
  const editMode = searchParams.get('edit') === 'true';

  const { campaign } = useCampaign();
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId: teamId!, campaignId: campaignId! });
  const updateIteration = useUpdateIteration();
  const navigate = useNavigate();

  useProtectRoute();
  const { data: campaignTemplate } = useFetchTemplateForCampaign({
    teamId: teamId!,
    campaignId: campaignId!,
    isEnabled: Boolean(campaign.CampaignConfig.Template),
  });

  const { referenceData } = useReferenceData();
  const { cohorts } = referenceData;

  const fetchedTeamDefaultRules = useFetchTeamDefaultRules({ teamId: teamId! });
  const teamDefaultRules = fetchedTeamDefaultRules.data;

  const { iteration, rule } = getIterationAndRuleByIDs(campaign, iterationId as string, ruleId as string, andClauseNumber);
  const handleSuccessfulSubmission = async ({ ruleFormValues, navigateToAndClause }: { ruleFormValues: RuleFormValues, navigateToAndClause: boolean }) => {
    const payload = {
      IterationID: iterationId as string,
      ID: ruleId === 'new' ? uuidv4() : ruleId,
      Type: ruleTypeFromRoute,
      CohortLabel: ruleFormValues.CohortLabel,
      Name: ruleFormValues.Name,
      Description: ruleFormValues.Description,
      Priority: ruleFormValues.Priority,
      Operator: ruleFormValues.Operator,
      Comparator: ruleFormValues.Comparator,
      AttributeTarget: ruleFormValues.AttributeTarget,
      AttributeLevel: ruleFormValues.AttributeLevel,
      AttributeName: ruleFormValues.AttributeName,
      AndNumber: andClauseNumber ? parseInt(andClauseNumber, 10) : 0,
      ...(ruleTypeFromRoute === 'R' ? { CommsRouting: rule?.CommsRouting || '' } : {}),
      LibraryRuleMetadata: ruleFormValues.LibraryRuleMetadata,
    };

    const updatedCampaign = updateRule(campaign, payload as UpdateRulePayload);

    try {
      setIsSubmitting(true);
      await updateIteration.mutateAsync({
        campaignId: campaignId!,
        iteration: getIterationById(updatedCampaign, iterationId!) as IterationDetailsFormValues,
        iterationId: iterationId!,
        teamId: teamId!,
      });

      const refetchResult = await fetchSpecificCampaign.refetch();
      const foundIteration = getIterationById(refetchResult.data!, iterationId!);
      if (!foundIteration || !foundIteration.IterationRules) {
        throw new Error('Iteration or rules not found.');
      }
      const foundPersistedRule = foundIteration.IterationRules.find((iterationRule) => iterationRule.ID === payload.ID);
      if (!foundPersistedRule) {
        throw new Error('Persisted rule not found.');
      }

      const navigateHref = buildHrefForSuccessfulRuleSubmission(
        updatedCampaign,
        iterationId as string,
        teamId!,
        andClauseNumber,
        payload.Priority,
        foundPersistedRule.ID!,
        ruleTypeFromRoute,
        navigateToAndClause,
      );
      navigate(navigateHref);
    } catch (err: unknown) {
      const message = err instanceof Error
        ? err.message
        : API_UPDATE_RULES_ERROR;

      setError({ message });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (ruleId === 'new' && state?.libraryRule === undefined && state?.addCustomRule !== true) {
    // Cannot create a new rule without selecting a library rule or adding a custom rule.
    navigate(`${location.pathname}/library`);
  }

  if (!iteration) {
    return <ProgressIndicator text="Loading rule details..." />;
  }

  if (andClauseNumber && !parseInt(andClauseNumber, 10)) {
    return <ProgressIndicator text="Loading rule details..." />;
  }

  if (campaignTemplate?.[RULE_TYPE_DISPLAY_KEY[ruleTypeFromRoute]] === 'hidden') {
    navigate(`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary`);
    return null;
  }

  const pageTitle = (andClauseNumber && rule)
    ? `Set up ‘AND’ clause ${andClauseNumber} for ${rule.Name}`
    : `Add a new ${ruleTypeMap[ruleTypeFromRoute].toLowerCase()} rule`;

  const handleNavigateToNewAndClause = () => {
    if (rule) {
      const andClauses = (iteration.IterationRules || [])
        .filter((r) => r.Priority === rule.Priority
                       && r.Type === rule.Type
                       && r.AndNumber !== 0);

      const nextAndClauseNumber = andClauses.length + 1;

      navigate(`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/${buildRulePathParam(rule.Type)}/${rule.ID}/and-clause/${nextAndClauseNumber}/library`);
    }
  };

  return (
    <>
      <div className="nhsuk-u-margin-bottom-6">
        <Back />
      </div>

      <IterationPageHeader iteration={iteration} title={pageTitle} />

      {!rule && ruleId !== 'new' && (
        <Card>
          <p>
            Cannot find rule, creating new rule...
          </p>
        </Card>
      )}

      <RuleForm
        onSuccessfulSubmission={handleSuccessfulSubmission}
        onNavigateToNewAndClause={handleNavigateToNewAndClause}
        currentIterationRule={rule}
        selectedLibraryRule={state?.libraryRule}
        addCustomRule={state?.addCustomRule}
        existingRules={iteration.IterationRules ?? []}
        ruleTypeFromRoute={ruleTypeFromRoute}
        andClauseNumber={andClauseNumber ? parseInt(andClauseNumber, 10) : undefined}
        iterationCohorts={(iteration?.IterationCohorts || [])}
        withCohortLabel={ruleTypeFromRoute !== 'R'}
        allCohorts={cohorts}
        teamDefaultRules={teamDefaultRules}
        isSubmitting={isSubmitting}
        editMode={editMode}
      />
      <ApiErrorDetails apiError={error || updateIteration.error} />
    </>
  );
}
